/**
 * Sprint V103.ch · Block A — Challenge-Dashboard-Kachel.
 *
 * Rendert NUR wenn `/api/challenge/state` {is_member: true} liefert.
 *
 * Negativtest-Garantie: Kein `is_member` -> Komponente rendert NICHTS (null).
 * Ein Free-Account OHNE Beitritts-Merkmal sieht keinen DOM-Node, keine leere
 * Kachel, keinen toten Reiter.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Flame, Lock, CheckCircle2, ArrowRight } from "lucide-react";

export default function ChallengeDashboardTile() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let cancelled = false;
    api.get("/challenge2/state")
      .then((r) => {
        if (!cancelled) setState({ loading: false, data: r.data });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, data: null });
      });
    return () => { cancelled = true; };
  }, []);

  // Negativtest: kein Member -> KEINEN DOM-Node rendern
  if (state.loading) return null;
  if (!state.data?.is_member) return null;

  const data = state.data;
  const totalDays = data.total_days || 5;
  const unlocked = data.days_unlocked || 0;
  const currentDay = data.current_day;
  const hasStarted = data.has_started;
  const startAt = data.start_at;
  const startLabel = startAt
    ? new Date(startAt).toLocaleString("de-DE", {
        weekday: "long", day: "2-digit", month: "2-digit",
        hour: "2-digit", minute: "2-digit",
      })
    : null;

  const entriesCount = (data.entries || []).filter((e) => e.text && e.text.trim()).length;

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-[#F3651D]/10 via-amber-50 to-white dark:from-[#F3651D]/20 dark:via-slate-900 dark:to-slate-900 border-2 border-[#F3651D]/40 shadow-md p-5 sm:p-6"
      data-testid="challenge-dashboard-tile"
      data-days-unlocked={unlocked}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#F3651D] text-white flex items-center justify-center shadow">
          <Flame className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <p className="text-xs font-bold uppercase tracking-widest text-[#F3651D]">
            5-Tage-Challenge
          </p>
          <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 dark:text-white mt-0.5">
            {hasStarted
              ? (currentDay ? `Tag ${currentDay} ist offen.` : "Deine Challenge läuft.")
              : "Challenge startet in Kürze."}
          </h3>
        </div>
      </div>

      {!hasStarted && startLabel && (
        <p className="text-sm text-slate-700 dark:text-slate-200 mb-4" data-testid="challenge-tile-start-info">
          Tag 1 öffnet am <b className="text-slate-900 dark:text-white">{startLabel}</b>.
          Du bekommst eine Erinnerungs-Mail.
        </p>
      )}

      {/* Fortschrittsbalken: 5 Tage als Punkte */}
      <div className="flex items-center gap-2 mb-5" data-testid="challenge-tile-progress">
        {Array.from({ length: totalDays }).map((_, idx) => {
          const dayNum = idx + 1;
          const isUnlocked = dayNum <= unlocked;
          const hasEntry = (data.entries || []).some((e) => e.day === dayNum && e.text?.trim());
          return (
            <div
              key={dayNum}
              className={
                "flex-1 h-9 rounded-lg flex items-center justify-center text-xs font-bold border-2 "
                + (isUnlocked
                  ? (hasEntry
                      ? "bg-emerald-500 border-emerald-600 text-white"
                      : "bg-white dark:bg-slate-800 border-[#F3651D] text-[#F3651D]")
                  : "bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-400")
              }
              data-testid={`challenge-tile-day-${dayNum}`}
              data-unlocked={isUnlocked}
              data-has-entry={hasEntry}
            >
              {hasEntry ? <CheckCircle2 className="w-4 h-4" /> : (isUnlocked ? dayNum : <Lock className="w-3 h-3" />)}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-slate-600 dark:text-slate-400">
          {entriesCount} von {totalDays} Tagen bearbeitet
        </p>
        {hasStarted && currentDay ? (
          <button
            type="button"
            onClick={() => navigate(`/challenge/tag/${currentDay}`)}
            data-testid="challenge-tile-cta"
            className="inline-flex items-center gap-1 rounded-xl bg-[#F3651D] hover:bg-[#d95415] text-white font-bold px-4 py-2 text-sm"
          >
            Tag {currentDay} öffnen <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            disabled
            data-testid="challenge-tile-cta-locked"
            className="inline-flex items-center gap-1 rounded-xl bg-slate-200 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-bold px-4 py-2 text-sm cursor-not-allowed"
          >
            <Lock className="w-3 h-3" /> Noch verschlossen
          </button>
        )}
      </div>
    </div>
  );
}
